export interface CardFieldOption {
  label: string;
  value: string;
}

export interface CardFieldCondition {
  field: string;
  operator: "equals" | "notEquals" | "contains";
  value: unknown;
}

export interface CardRegistrationField {
  name: string;
  label: string;
  type: string;
  order: number;
  options?: CardFieldOption[];
  conditional?: CardFieldCondition;
}

export interface LibraryCardPrintFieldRef {
  label: string;
  formDataKey: string;
}

export interface LibraryCardFieldLine {
  key: string;
  label: string;
  value: string;
}

const RESERVED_FIELD_NAMES = new Set([
  "email",
  "password",
  "confirmPassword",
  "firstName",
  "lastName",
  "phone",
]);

const OPTION_FIELD_TYPES = new Set(["select", "radio"]);

function fieldIsVisible(field: CardRegistrationField, data: Record<string, unknown>): boolean {
  const conditional = field.conditional;
  if (!conditional) return true;
  const fieldValue = data[conditional.field];
  switch (conditional.operator) {
    case "equals":
      return fieldValue === conditional.value;
    case "notEquals":
      return fieldValue !== conditional.value;
    case "contains":
      return String(fieldValue ?? "").includes(String(conditional.value));
    default:
      return true;
  }
}

function optionLabel(field: CardRegistrationField, raw: string): string {
  if (!OPTION_FIELD_TYPES.has(field.type)) return raw;
  const match = field.options?.find((option) => option.value === raw);
  const label = match?.label?.trim();
  return label || raw;
}

export function formatRegistrationFieldValue(field: CardRegistrationField, raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "boolean") return raw ? "Yes" : "";
  if (typeof raw === "number") return Number.isFinite(raw) ? String(raw) : "";
  if (Array.isArray(raw)) {
    return raw
      .map((item) => optionLabel(field, String(item).trim()))
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .join(", ");
  }
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return optionLabel(field, trimmed);
}

function toLine(
  field: CardRegistrationField,
  label: string,
  data: Record<string, unknown>
): LibraryCardFieldLine | null {
  if (!field.name || field.type === "file" || RESERVED_FIELD_NAMES.has(field.name)) return null;
  if (!fieldIsVisible(field, data)) return null;
  const value = formatRegistrationFieldValue(field, data[field.name]);
  if (!value) return null;
  return { key: field.name, label: label.trim() || field.label.trim() || field.name, value };
}

export function resolveLibraryCardFields(input: {
  formData: Record<string, unknown> | undefined;
  patronFields: CardRegistrationField[];
  printFields: LibraryCardPrintFieldRef[];
}): LibraryCardFieldLine[] {
  const data = input.formData ?? {};
  const byName = new Map<string, CardRegistrationField>();
  for (const field of input.patronFields) {
    const name = field.name?.trim();
    if (!name || byName.has(name)) continue;
    byName.set(name, { ...field, name });
  }

  const lines: LibraryCardFieldLine[] = [];
  const seen = new Set<string>();
  const push = (field: CardRegistrationField, label: string) => {
    if (seen.has(field.name)) return;
    const line = toLine(field, label, data);
    if (!line) return;
    seen.add(field.name);
    lines.push(line);
  };

  for (const row of input.printFields) {
    const key = row.formDataKey?.trim();
    if (!key) continue;
    const field = byName.get(key);
    if (!field) continue;
    push(field, row.label || field.label);
  }

  const remaining = [...byName.values()].sort((a, b) => a.order - b.order);
  for (const field of remaining) push(field, field.label);
  return lines;
}

export async function loadRegistrationFieldsByPatronType(
  patronTypeIds: string[],
  fetchConfig: (patronTypeId?: string) => Promise<{ formFields: CardRegistrationField[] }>
): Promise<Record<string, CardRegistrationField[]>> {
  const general = await fetchConfig(undefined);
  const loaded: Record<string, CardRegistrationField[]> = {
    "": general.formFields,
  };
  const uniqueIds = [...new Set(patronTypeIds.map((id) => id.trim()).filter(Boolean))];
  await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const config = await fetchConfig(id);
        loaded[id] = config.formFields;
      } catch {
        loaded[id] = general.formFields;
      }
    })
  );
  return loaded;
}
